import { useTranslation } from 'react-i18next';
import axios from 'axios';
import P_SearchHeader from '../common/searchHeader/P_SearchHeader';
import Button from 'src/ui/wards/common/button/Button';
import Input from 'src/ui/wards/common/input/Input';
import { MagnifyingGlassCircleIcon } from '@heroicons/react/24/solid';
import DataTable from 'react-data-table-component';
import { unaddressedSiteColumns, unaddressedSiteTableStyle } from './UnaddressedPropertiesColumns';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { useNavigate } from 'react-router-dom';
import { Workplace, WorkplaceReservationToCreate } from 'src/api/wa/store/models/workplace';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useState, useEffect, useCallback } from 'react';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import Alert from 'src/ui/common/alert/Alert';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { useNotifyWithParam } from 'src/ui/common/alert/useNotifyWithParam';
import { LoadingSpinner } from '../common/loading/LoadingSpinner';

const P5_2_UnaddressedPropertiesList = () => {
  const { t } = useTranslation();
  const { createWorkplaceReservationWithoutRefresh, cancelWorkplaceReservation, fetchWorkplaces } =
    useWaWorkplaceService();
  const { setSelectedWorkplace, workplaces, setWorkplaces } = useWaWorkplaceStore();
  const { selectedUser } = useWaUserStore();
  const navigate = useNavigate();

  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  const [toggler, setToggler] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Workplace[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { areMandatorySearchParametersSet } = useWaFilterStore();
  const { selectedDisaster } = useWaDisasterStore();
  const notify = useNotify();
  const notifyWithParam = useNotifyWithParam();

  useEffect(() => {
    if (selectedDisaster && areMandatorySearchParametersSet() && !isSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDisaster]);

  const handleSearch = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    setWorkplaces(null);
    try {
      await fetchWorkplaces();
      setIsSearched(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      setWorkplaces(null);
    };
  }, [setWorkplaces]);

  const isBatchReservationDisabled = selectedRows.length > 20;

  const handleBatchReservation = async () => {
    if (isSubmitting) return;

    setBatchError(null);

    if (selectedRows.length === 0) {
      setBatchError(t('wards.desktop.unaddressedPropertiesMap.noPropertySelected'));
      return;
    }

    if (!selectedUser?.id) {
      setBatchError(t('wards.desktop.unaddressedPropertiesMap.noWorkerSelected'));
      return;
    }

    const localErrors: string[] = [];
    const localSkipped: string[] = [];

    setIsSubmitting(true);

    try {
      await Promise.all(
        selectedRows.map(async (workplace) => {
          const reservationsToCreate: WorkplaceReservationToCreate[] = [];

          if ((workplace.details.reservations?.length ?? 0) === 0) {
            reservationsToCreate.push({
              engineerUserId: selectedUser.id,
              customerId: workplace.customerId,
              branchCode: workplace.branchCode,
            });

            try {
              const res = await createWorkplaceReservationWithoutRefresh(reservationsToCreate);
              if (!res) {
                localErrors.push(workplace.customerId);
              }
            } catch (e) {
              console.warn(`Error reserving customer ${workplace.customerId}:`, e);
              localErrors.push(workplace.customerId);
            }
          } else {
            localSkipped.push(workplace.customerId);
          }
        })
      );

      if (localErrors.length > 0) {
        notifyWithParam('wards.sites.reservationFailed', 'workplaces', localErrors.join(', '));
      } else if (selectedRows.length === localSkipped.length) {
        notify('wards.sites.allAlreadyReserved');
      } else {
        notify('wards.sites.reservationSuccess');
      }

      await handleSearch();
      setSelectedRows([]);
      setToggler((prev) => !prev);
    } catch (error) {
      console.error('Batch reservation critical error:', error);
      notify('wards.common.updateError');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateToUserSearch = () =>
    wardsNavigate(navigate, WaScreensMap[WA_Screen.UserSearch].path);

  const isRowSelected = useCallback(
    (row: Workplace) => {
      return selectedRows.some((selected) => selected.customerId === row.customerId);
    },
    [selectedRows]
  );

  const handleRowSelected = useCallback(
    (state: { selectedRows: Workplace[] }) => {
      const { selectedRows: newSelectedRows } = state;

      if (newSelectedRows.length === selectedRows.length) {
        return;
      }

      if (newSelectedRows.length > 20) {
        notify(t('wards.common.reservationLimitExceeded'));

        setToggler((prev) => !prev);
        return;
      }

      setSelectedRows(newSelectedRows);
      setBatchError(null);
    },
    [selectedRows, t, notify]
  );

  return (
    <div className="h-full space-y-2">
      <div className="space-y-1 text-2xl font-bold">
        {t('wards.desktop.unaddressedPropertiesList.title')}
      </div>
      <P_SearchHeader showListButton={false} showMapButton={true} onSearch={handleSearch} />
      <div className="flex flex-col overflow-hidden rounded-md border-t-2 border-sky-600 bg-gray-100 py-2">
        <div className="px-3 py-2">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2 pb-2">
            <Button
              title={t('wards.desktop.unaddressedPropertiesList.clear')}
              className="mt-2 w-fit rounded-md border border-gray-400 bg-gradient-to-b from-gray-100 to-gray-300 px-1 text-gray-800"
              onClick={() => {
                setSelectedRows([]);
                setToggler(!toggler);
              }}
            />
            <FormRow title={t('wards.desktop.unaddressedPropertiesList.candidate')}>
              <div className="flex items-center">
                <Input className="h-8 rounded-r-none" value={selectedUser?.name ?? ''} disabled />
                <Button
                  iconOnly
                  startIcon={<MagnifyingGlassCircleIcon className="size-5" />}
                  onClick={handleNavigateToUserSearch}
                />
              </div>
            </FormRow>
            <Button
              title={t('wards.desktop.unaddressedPropertiesList.batchReservation')}
              className="mt-2 w-fit rounded-md border border-gray-400 bg-gradient-to-b from-gray-100 to-gray-300 px-1 text-gray-800"
              onClick={handleBatchReservation}
              disabled={isBatchReservationDisabled || isSubmitting}
            />
            <div className="flex flex-col text-sm font-medium text-red-600">
              {batchError && <p>{batchError}</p>}
              {isBatchReservationDisabled && <p>{t('wards.common.reservationLimitExceeded')}</p>}
            </div>
          </div>

          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <DataTable
              keyField="customerId"
              data={isSearched && workplaces ? workplaces.slice(0, 100) : []}
              columns={unaddressedSiteColumns(
                navigate,
                t,
                setSelectedWorkplace,
                cancelWorkplaceReservation,
                notify
              )}
              customStyles={unaddressedSiteTableStyle}
              noDataComponent={
                isTimeout
                  ? t('wards.common.searchTimeout')
                  : isSearched
                    ? t('wards.common.noResults')
                    : ''
              }
              selectableRows
              selectableRowSelected={isRowSelected}
              selectableRowsHighlight
              clearSelectedRows={toggler}
              onSelectedRowsChange={handleRowSelected}
            />
          )}
          {alertMessage !== '' && (
            <Alert message={alertMessage} onClose={() => setAlertMessage('')} />
          )}
        </div>
      </div>
    </div>
  );
};

export default P5_2_UnaddressedPropertiesList;
